import React from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { AddFromFriendsWordFading } from '../AddFromFriendsWordFading';
import { ButtonV2, Flex, FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import { AddFromFriendsRegistryScannerDialog } from '../AddFromFriendsRegistryScannerDialog';
import { useAddFromFriendsRegistryBannerController } from './AddFromFriendsRegistryBanner.controller';
import { AFFR_BANNER_INPUT_SECTION_ID, AFFR_BANNER_INPUT_INPUT_ID } from '../../FriendsRegistry.constants';
import {
  styles,
  StyledHelpIcon,
  StyledWrapper,
  StyledHeading,
  StyledFormWrapper,
  StyledBackgroundVideo,
  StyledMediaContainer,
  StyledTextContainer
} from './AddFromFriendsRegistryBanner.styles';

export type FriendsRegistryBannerVariant = 'default' | 'animated';
export type FriendsRegistryTelemetryPageName = 'indexPage' | 'shopRootPage' | 'registryOverviewPage';

interface AddFromFriendsRegistryBannerProps {
  eventId: string;
  variant?: FriendsRegistryBannerVariant;
  telemetryPageName?: FriendsRegistryTelemetryPageName;
  onHowItWorksClick?: () => void;
}

export const AddFromFriendsRegistryBanner: React.FC<AddFromFriendsRegistryBannerProps> = ({ eventId, variant = 'default', telemetryPageName = 'indexPage', onHowItWorksClick }) => {
  const {
    formik,
    telemetry,
    loading,
    translations,
    videoUrl,
    videoThumbnailUrl,
    isScannerDialogOpen,
    registryInfo,
    getFieldErrorProps,
    handleHowItWorks,
    handleOnCloseDialog
  } = useAddFromFriendsRegistryBannerController(eventId, telemetryPageName, onHowItWorksClick);
  return (
    <StyledWrapper __css={styles.wrapper(variant)} id={AFFR_BANNER_INPUT_SECTION_ID}>
      {variant === 'animated' && (
        <StyledMediaContainer __css={styles.mediaContainer}>
          <StyledBackgroundVideo __css={styles.backgroundVideo(videoThumbnailUrl)}>
            <iframe src={videoUrl} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title={translations.title} />
          </StyledBackgroundVideo>
        </StyledMediaContainer>
      )}
      <StyledTextContainer __css={styles.textContainer(variant)}>
        <StyledHeading __css={styles.heading}>
          <TextV2 typographyVariant={['hed2', null, null, 'hed4']} color="mono10">
            {translations.eyebrow}
          </TextV2>
          <TextV2 typographyVariant={['display1', null, null, 'display4']} marginTop={3} fontSize={[null, null, null, pxToRem(48)]} lineHeight={[null, null, null, pxToRem(57.6)]}>
            {translations.title}
          </TextV2>
          <TextV2 as="div" typographyVariant={['display1', null, null, 'display4']} fontSize={[null, null, null, pxToRem(48)]} lineHeight={[null, null, null, pxToRem(57.6)]}>
            <AddFromFriendsWordFading />
          </TextV2>
          {variant === 'animated' && (
            <TextV2 typographyVariant="body3" display="flex" color="mono12" marginTop={5}>
              {translations.content}
            </TextV2>
          )}
        </StyledHeading>
        <form onSubmit={formik.handleSubmit}>
          <StyledFormWrapper __css={styles.formWrapper}>
            <FormControl
              htmlFor="registry-url"
              onFocus={() => telemetry.inputHandler('Registry URL', 'onFocus')}
              {...getFieldErrorProps('registryUrl')}
              label={
                <Flex columnGap={2}>
                  <TextV2 typographyVariant="button1" alignItems="center" display="flex">
                    {translations.inputTitle}
                  </TextV2>
                  <StyledHelpIcon color="#767676" onClick={() => handleHowItWorks()} />
                </Flex>
              }
            >
              <InputV2 placeholder={translations.inputPlaceholder} {...formik.getFieldProps('registryUrl')} id={AFFR_BANNER_INPUT_INPUT_ID} />
            </FormControl>
            <ButtonV2 intent="neutral" marginTop={7} type="submit" loading={loading}>
              {translations.ctaText}
            </ButtonV2>
          </StyledFormWrapper>
        </form>
      </StyledTextContainer>
      {registryInfo.id && <AddFromFriendsRegistryScannerDialog {...registryInfo} isOpen={isScannerDialogOpen} onClose={handleOnCloseDialog} />}
    </StyledWrapper>
  );
};
